import * as React from "react";

/**
 * Checks if the Clerk SDK has loaded on the client side.
 * Used to conditionally render auth components that depend on Clerk.
 */
export function hasClerkLoaded(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof (window as typeof window & { Clerk?: unknown }).Clerk !== "undefined"
  );
}

/**
 * Hook that polls for Clerk SDK availability.
 * Returns null during SSR/initial load, true when Clerk is available,
 * or false after max attempts are exhausted.
 */
export function useClerkLoaded(
  maxAttempts: number = 20,
  intervalMs: number = 100,
): boolean | null {
  const [hasClerk, setHasClerk] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    if (hasClerkLoaded()) {
      setHasClerk(true);
      return;
    }

    let attempts = 0;
    const checkClerk = setInterval(() => {
      if (hasClerkLoaded()) {
        setHasClerk(true);
        clearInterval(checkClerk);
      } else if (attempts >= maxAttempts - 1) {
        setHasClerk(false);
        clearInterval(checkClerk);
      }
      attempts++;
    }, intervalMs);

    return () => clearInterval(checkClerk);
  }, [maxAttempts, intervalMs]);

  return hasClerk;
}
